import React, { useState, useEffect } from "react";
import styles from "./statusBanner.module.scss";

// Ported from the SHP v3 bus status banner (shpV3:
// client/src/components/selfHelp/statusBanner) for FM/LM cabs.
const METRIC_SWAP_INTERVAL = 10 * 1000;
// Zing sends cab pings every few minutes, so this is wider than the bus's 5
const LOCATION_STALE_AFTER = 10 * 60 * 1000;

const getEtaMinutes = eta =>
  eta && eta.etaSec ? Math.max(1, Math.round(eta.etaSec / 60)) : null;

const getEtaKms = eta =>
  eta && eta.distanceMeters
    ? Math.max(1, Math.round(eta.distanceMeters / 1000))
    : null;

const getStatus = ({ eta, location }) => {
  if (location && Date.now() - location.capturedAt > LOCATION_STALE_AFTER) {
    return {
      showIcon: true,
      title: "Cab location unavailable",
      subtitle:
        "Cab location is currently unavailable, showing the last updated location"
    };
  }

  // DROP: after boarding - FM to the change stop, LM to the drop points
  const isDrop = eta.leg === "DROP";
  const pointType = isDrop ? "drop point" : "boarding point";
  const pointName = `the ${eta.target.name} ${pointType}`;

  // LM with no drop data: the cab has left the pickup with the passengers
  if (eta.phase === "LEFT" && !isDrop && eta.serviceType === "LASTMILE") {
    return {
      showIcon: true,
      title: "Cab on the way",
      subtitle: `The cab has left ${pointName}.`
    };
  }

  if (eta.phase === "REACHED" || eta.phase === "LEFT") {
    return {
      showIcon: true,
      title: `Cab reached the ${pointType}`,
      subtitle: `The cab has reached ${pointName}.`
    };
  }

  // Same "Delayed by X mins" as Tracer's bus tracking
  if (eta.delay) {
    const minutes = getEtaMinutes(eta);
    return {
      title: `Delayed by ${eta.delayMinutes} mins`,
      subtitle: minutes
        ? `The cab will reach ${pointName} in ${minutes} mins`
        : "The cab is running behind schedule."
    };
  }

  return {
    title: isDrop
      ? "Cab arriving at your drop point"
      : "Cab arriving to boarding point",
    subtitle: `The cab will be arriving soon at ${pointName}.`
  };
};

const StatusBanner = ({ eta, location }) => {
  const [showDistance, setShowDistance] = useState(false);

  const minutes = eta && eta.phase === "EN_ROUTE" ? getEtaMinutes(eta) : null;
  const kms = eta && eta.phase === "EN_ROUTE" ? getEtaKms(eta) : null;
  const canSwap = !!minutes && !!kms;

  useEffect(() => {
    if (!canSwap) {
      return undefined;
    }

    const timer = setInterval(
      () => setShowDistance(current => !current),
      METRIC_SWAP_INTERVAL
    );

    return () => clearInterval(timer);
  }, [canSwap]);

  if (!eta || !eta.target) {
    return null;
  }

  const status = getStatus({ eta, location });
  const showKms = showDistance && canSwap;

  return (
    <div className={styles.banner}>
      {status.showIcon || !minutes ? (
        <div className={styles.iconBox}>
          <div className={styles.spinner}>
            <span />
            <span />
            <span />
            <span />
          </div>
        </div>
      ) : (
        <div className={styles.etaBox}>
          <span className={styles.etaValue}>{showKms ? kms : minutes}</span>
          <span className={styles.etaUnit}>{showKms ? "kms" : "mins"}</span>
        </div>
      )}
      <div className={styles.content}>
        <span className={styles.title}>{status.title}</span>
        <span className={styles.subtitle}>{status.subtitle}</span>
      </div>
    </div>
  );
};

export default StatusBanner;
